import type { ReactFormExtendedApi } from "@tanstack/react-form";
import z from "zod";

export interface SiteSetupProps {
  onComplete?: (site?: Site) => void | Promise<void>;
  onCancel?: () => void;
  site?: Site;
  className?: string;
  formId?: string;
  showActions?: boolean;
  geofenceHelperText?: string;
}

export interface AddressSuggestionItem {
  placeId: string;
  description: string;
  primaryText: string;
  secondaryText?: string | null;
}

export interface AddressSuggestionsProps {
  items: Array<AddressSuggestionItem>;
  selectedIndex: number | null;
  setSelectedIndex: (i: number | null) => void;
  onSelect: (value: AddressSuggestionItem) => void;
}

export type SiteSetupFormValues = {
  siteName: string;
  address: string;
  geofenceDistance: string;
  lat: number | null;
  lon: number | null;
  plusCode: string;
};

export const siteGeofenceOptions = ["50", "100", "200", "500", "1000"] as const;

export const siteSetupFormSchema = z
  .object({
    siteName: z
      .string()
      .trim()
      .min(2, "Site name must be at least 2 characters"),
    address: z.string().trim().min(5, "Address must be at least 5 characters"),
    geofenceDistance: z
      .string()
      .refine(
        (value) =>
          siteGeofenceOptions.includes(
            value as (typeof siteGeofenceOptions)[number]
          ),
        "Select a geofence distance"
      ),
    lat: z.number().nullable(),
    lon: z.number().nullable(),
    plusCode: z.string(),
  })
  .superRefine((value, ctx) => {
    const hasCoordinates =
      typeof value.lat === "number" &&
      Number.isFinite(value.lat) &&
      typeof value.lon === "number" &&
      Number.isFinite(value.lon);
    const hasPlusCode = value.plusCode.trim().length > 0;

    if (!hasCoordinates && !hasPlusCode) {
      ctx.addIssue({
        code: "custom",
        path: ["address"],
        message:
          "Select an address from the list or enter coordinates manually",
      });
    }
  });

// biome-ignore lint/suspicious/noExplicitAny: TanStack Form infers validator generics from the concrete useForm call, so this alias stays intentionally broad.
type AnyFormValidator = any;

export type SiteSetupFormApi = ReactFormExtendedApi<
  SiteSetupFormValues,
  AnyFormValidator,
  AnyFormValidator,
  AnyFormValidator,
  AnyFormValidator,
  AnyFormValidator,
  AnyFormValidator,
  AnyFormValidator,
  AnyFormValidator,
  AnyFormValidator,
  AnyFormValidator,
  unknown
>;

export interface Site {
  id: string;
  name: string;
  address?: string | null;
  lat?: number | null;
  lon?: number | null;
  plusCode?: string | null;
  publicCode?: string | null;
  geofenceDistance: number;
  entrances?: SiteEntrance[];
}

export interface SiteEntrance {
  id?: string;
  siteId?: string;
  name: string;
  lat: number;
  lon: number;
  plusCode?: string | null;
  publicCode?: string | null;
  geofenceDistance: number;
  isPrimary?: boolean;
}
